import { cn } from '@/lib/utils';

/**
 * Page primitives — the layout contract between the app shell and every
 * routed page.
 *
 * The shell (RootLayout) gives each page a viewport-bounded box; these
 * primitives decide what happens when content doesn't fit, so pages
 * never hand-roll the `flex-1 min-h-0 overflow` dance themselves:
 *
 *   <Page>                          bounded column filling the shell slot
 *       <PageHeader>…</PageHeader>  optional, always visible
 *       <PageBody scroll="y">…      the ONE scroll decision a page makes
 *       <PageFooter>…</PageFooter>  optional, always visible
 *   </Page>
 *
 * `scroll="y"` — document-like pages (settings, lists): the body is the
 * scroll container, header/footer stay pinned.
 * `scroll="none"` — app-like pages (wizards, workspaces): the page can
 * NOT overflow; content that doesn't fit is a visible layout bug in
 * dev, not a shipped scrollbar. The page owns its internal scroll
 * regions instead.
 */

interface PageProps {
    className?: string;
    children: React.ReactNode;
}

export function Page({ className, children }: PageProps) {
    return <div className={cn('flex h-full min-h-0 flex-col', className)}>{children}</div>;
}

export function PageHeader({ className, children }: { className?: string; children: React.ReactNode }) {
    return <header className={cn('shrink-0', className)}>{children}</header>;
}

interface PageBodyProps {
    /**
     * 'y'    — body scrolls vertically (document-like pages).
     * 'none' — body is a bounded flex column; overflow is clipped so
     *          layout bugs surface immediately (app-like pages).
     */
    scroll?: 'y' | 'none';
    /**
     * Default content padding. Lives on the body (inside the scroll
     * container) so the scrollbar hugs the viewport edge; disable for
     * workspace screens that manage their own edge spacing.
     */
    padded?: boolean;
    className?: string;
    children: React.ReactNode;
}

export function PageBody({ scroll = 'y', padded = true, className, children }: PageBodyProps) {
    return (
        <div
            className={cn(
                'min-h-0 flex-1',
                scroll === 'y' ? 'overflow-y-auto' : 'flex flex-col overflow-hidden',
                padded && 'p-4',
                className
            )}
        >
            {children}
        </div>
    );
}

export function PageFooter({ className, children }: { className?: string; children: React.ReactNode }) {
    return <footer className={cn('shrink-0 border-t bg-background', className)}>{children}</footer>;
}
